import {
  afterNextRender,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, NgControl } from '@angular/forms';

import { groupThousands } from '../services/number-format';

/**
 * Keeps any FormControl-bound text field digit-only WITHOUT type=number (which would still
 * accept "e", ".", ",", "+/-" and add spinner arrows). Three layers:
 *  - inputmode="numeric" + pattern="[0-9]*" hint the mobile numeric keypad;
 *  - (keydown) blocks non-digit printable keys up front (nav/edit/clipboard keys pass);
 *  - a valueChanges sanitizer strips whatever slips in via paste / autofill / IME.
 *
 * Drop `wppNumericInput` on the element — e.g. `<core-ui-text-input wppNumericInput …>`. Add
 * `[wppGroupThousands]="true"` to render the value Dutch-grouped ("123.456") while typing;
 * the page derives the raw digits/number for validation and submit. Group mode also lets the
 * user type "." / "," (normalised away into the grouping); strict mode blocks them.
 */
@Directive({
  selector: '[wppNumericInput]',
})
export class NumericInputDirective implements OnInit {
  // Prefixed property name (not an alias) so the template binds [wppGroupThousands] without
  // tripping @angular-eslint/no-input-rename.
  public readonly wppGroupThousands = input<boolean>(false);

  private readonly ngControl: NgControl | null = inject(NgControl, { self: true, optional: true });
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public constructor() {
    // core-ui-text-input renders a type=text <input>; reach it after first render to hint the
    // numeric keypad + pattern without switching to type=number.
    afterNextRender((): void => {
      const host: HTMLElement = this.elementRef.nativeElement;
      const input: HTMLInputElement | null =
        host instanceof HTMLInputElement ? host : host.querySelector('input');
      if (input) {
        input.inputMode = 'numeric';
        input.setAttribute('pattern', '[0-9]*');
      }
    });
  }

  public ngOnInit(): void {
    const control: AbstractControl | null = this.ngControl?.control ?? null;
    if (!control) {
      return;
    }

    // Backstop for paste / autofill / IME: strip to digits (and re-group in group mode),
    // re-emitting the cleaned value (emitEvent:true so bound signals see it; the equality
    // guard against the final form stops the loop).
    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | number | null): void => {
        if (value == null) {
          return;
        }
        const digitsOnly: string = String(value).replace(/\D/g, '');
        const next: string = this.wppGroupThousands() ? groupThousands(digitsOnly) : digitsOnly;
        if (next !== String(value)) {
          control.setValue(next, { emitEvent: true });
        }
      });
  }

  // Block printable non-digits up front; digits, nav/edit keys and Ctrl/Cmd shortcuts pass.
  // In group mode "." / "," also pass (the sanitizer folds them into the grouping).
  // Paste/autofill is handled by the valueChanges sanitizer above.
  @HostListener('keydown', ['$event'])
  protected blockNonDigitKey(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length > 1) {
      return;
    }
    const isDigit: boolean = event.key >= '0' && event.key <= '9';
    const isGroupSeparator: boolean =
      this.wppGroupThousands() && (event.key === '.' || event.key === ',');
    if (!isDigit && !isGroupSeparator) {
      event.preventDefault();
    }
  }
}
